import React from 'react';
import { Table, Badge } from '@/components/ui';
import type { MaintenanceSchedule } from '../types/maintenance-schedules.types';

export interface ScheduleListViewProps {
  data: MaintenanceSchedule[];
  isLoading: boolean;
  onRowClick: (schedule: MaintenanceSchedule) => void;
}

export function ScheduleListView({ data, isLoading, onRowClick }: ScheduleListViewProps) {
  const getTaskTypeLabel = (taskType: string) => {
    const map: Record<string, string> = {
      mowing: 'Cắt cỏ',
      pruning: 'Cắt tỉa',
      watering: 'Tưới nước',
      fertilizing: 'Bón phân',
      spraying: 'Phun thuốc',
      cleaning: 'Dọn vệ sinh',
    };
    return map[taskType] || taskType;
  };

  const getStatusBadge = (status: MaintenanceSchedule['status']) => {
    switch (status) {
      case 'draft': return <Badge variant="neutral">Nháp</Badge>;
      case 'assigned': return <Badge variant="info">Đã phân công</Badge>;
      case 'in_progress': return <Badge variant="primary">Đang thực hiện</Badge>;
      case 'completed': return <Badge variant="success">Hoàn thành</Badge>;
      case 'delayed': return <Badge variant="danger">Trễ hạn</Badge>;
      case 'cancelled': return <Badge variant="neutral">Đã hủy</Badge>;
      default: return null;
    }
  };

  return (
    <Table
      columns={[
        { key: 'date', title: 'Ngày lịch' },
        { key: 'time', title: 'Thời gian', align: 'center' },
        { key: 'zone', title: 'Khu vực' },
        { key: 'task', title: 'Công việc' },
        { key: 'team', title: 'Tổ đội' },
        { key: 'wo', title: 'Work Order' },
        { key: 'status', title: 'Trạng thái', align: 'center' },
      ]}
      data={data}
      isLoading={isLoading}
      onRowClick={onRowClick}
      renderCell={(item, key) => {
        switch (key) {
          case 'date':
            return <span className="font-medium text-os-text">{new Date(item.scheduled_date).toLocaleDateString('vi-VN')}</span>;
          case 'time':
            return (
              <span className="text-sm text-os-muted">
                {item.start_time && item.end_time ? `${item.start_time} - ${item.end_time}` : 'Cả ngày'}
              </span>
            );
          case 'zone':
            return <span className="text-sm font-semibold">{item.zone_name}</span>;
          case 'task':
            return (
              <div className="flex flex-col">
                <span className="font-medium">{getTaskTypeLabel(item.task_type)}</span>
                {item.conflict_issues && item.conflict_issues.length > 0 && (
                  <span className="text-xs text-red-500 mt-0.5"><i className="ri-error-warning-line mr-1"></i>Có xung đột</span>
                )}
              </div>
            );
          case 'team':
            return (
              <span className="text-sm bg-slate-100 px-2 py-1 rounded text-os-text">
                {item.team_name || 'Chưa gán'}
              </span>
            );
          case 'wo':
            return item.work_order_id ? (
              <span className="text-xs font-mono text-brand-600 bg-brand-50 px-1.5 py-0.5 rounded border border-brand-200">
                {item.work_order_id}
              </span>
            ) : (
              <span className="text-xs text-slate-400 italic">Chưa tạo</span>
            );
          case 'status':
            return getStatusBadge(item.status);
          default:
            return String(item[key as keyof MaintenanceSchedule] || '');
        }
      }}
    />
  );
}
